import { X } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import Joyride, { CallBackProps, STATUS, Step, Locale, TooltipRenderProps } from 'react-joyride';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'next-themes';
import { useNavigate } from 'react-router-dom';
import { tours, ExtendedStep } from '../../config/onboardingTours';

import { Button } from '../Common/button';
import { useTourStore } from '../../stores/tourStore';
import { useAppName } from '../../hooks/useAppName';
import { useAuthStore } from '../../stores/authStore';
import { markOnboardingCompleted } from '../../services/userService';

interface OnboardingTourProps {
  tourId: string;
  onComplete?: () => void;
}

function isStepTargetInDOM(target: string | HTMLElement): boolean {
  if (typeof target !== 'string') return document.contains(target);
  if (target === 'body') return true;
  try {
    return document.querySelector(target) != null;
  } catch {
    return false;
  }
}

export const OnboardingTour = ({ tourId, onComplete }: OnboardingTourProps) => {
  const { t } = useTranslation();
  const appName = useAppName();
  const { theme, systemTheme } = useTheme();
  const navigate = useNavigate();
  const { stepIndex, setStep, nextStep, prevStep, finishTour, skipTour, resumeRequestedAt } = useTourStore();
  const user = useAuthStore((s) => s.user);
  const updateUser = useAuthStore((s) => s.actions.updateUser);
  const tour = tours[tourId];
  const [run, setRun] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const retryRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isDarkMode = theme === 'dark' || (theme === 'system' && systemTheme === 'dark');
  const isFirstRun = !!tour?.firstRun;

  // For first-run tours: when the user dismisses with "don't show again" checked,
  // persist the flag per-user (cross-device) and reflect it locally so it doesn't re-launch.
  const persistDismissIfNeeded = () => {
    if (!isFirstRun || !dontShowAgain) return;
    updateUser({ preferences: { ...(user?.preferences ?? {}), onboarding_completed: true } });
    void markOnboardingCompleted().catch(() => {
      /* best-effort; local flag still prevents re-launch this session */
    });
  };

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, index, type, action } = data;
    const isLastStep = index === tour.steps.length - 1;

    if (type === 'step:after' && action === 'next') {
      if (isLastStep) {
        localStorage.setItem(`tour-completed-${tourId}`, 'true');
        persistDismissIfNeeded();
        finishTour();
        onComplete?.();
        return;
      }
      const currentStep = tour.steps[index] as ExtendedStep;
      if (currentStep.navigateTo) {
        navigate(currentStep.navigateTo);
        setStep(index + 1);
      } else {
        nextStep();
      }
    } else if (type === 'step:after' && action === 'prev') {
      prevStep();
    }

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      if (status === STATUS.FINISHED) {
        localStorage.setItem(`tour-completed-${tourId}`, 'true');
      }
      persistDismissIfNeeded();
      status === STATUS.SKIPPED ? skipTour() : finishTour();
      onComplete?.();
    }
  };

  if (!tour) {
    console.warn(`Tour with id "${tourId}" not found`);
    return null;
  }

  const safeStepIndex = Math.min(Math.max(0, stepIndex), tour.steps.length - 1);

  // Only run Joyride when the current step's target exists in the DOM to avoid getBoundingClientRect(null).
  // We also scroll the target into the visible viewport ourselves: Établi's main content lives in an inner
  // overflow-y-auto container (not the window), which confuses Joyride's own scroll/measure logic and lands
  // the tooltip at document coordinates instead of viewport ones. Scrolling first keeps positioning correct.
  useEffect(() => {
    const target = tour.steps[safeStepIndex]?.target ?? 'body';
    const check = () => {
      if (!isStepTargetInDOM(target)) {
        setRun(false);
        return false;
      }
      if (typeof target === 'string' && target !== 'body') {
        document
          .querySelector(target)
          ?.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
      } else if (target instanceof HTMLElement) {
        target.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
      }
      setRun(true);
      return true;
    };

    if (!check() && target !== 'body') {
      retryRef.current = setTimeout(() => check(), 150);
    }
    return () => {
      if (retryRef.current) {
        clearTimeout(retryRef.current);
        retryRef.current = null;
      }
    };
  }, [tour, safeStepIndex]);

  // When user clicks "Reprendre" in the tour pill, re-show the tooltip (next frame so Joyride can open)
  useEffect(() => {
    if (resumeRequestedAt <= 0) return;
    const id = requestAnimationFrame(() => {
      setRun(true);
    });
    return () => cancelAnimationFrame(id);
  }, [resumeRequestedAt]);

  const translatedSteps: Step[] = tour.steps.map((step) => ({
    ...step,
    content: typeof step.content === 'string' ? t(step.content, { app: appName }) : step.content,
    title: step.title && typeof step.title === 'string' ? t(step.title, { app: appName }) : step.title,
  }));

  const locale: Locale = {
    back: t('global.back'),
    close: t('global.close'),
    last: t('onboarding.last'),
    next: t('global.next'),
    open: t('onboarding.open', 'Open'),
    skip: t('onboarding.skipShort'),
  };

  const CustomTooltip = ({
    continuous,
    index,
    step,
    backProps,
    closeProps,
    primaryProps,
    skipProps,
    tooltipProps,
    isLastStep,
  }: TooltipRenderProps) => {
    const totalSteps = translatedSteps.length;
    const currentStep = index + 1;

    return (
      <div
        {...tooltipProps}
        className={`
          ${isDarkMode ? 'bg-card text-card-foreground' : 'bg-white text-gray-900'}
          border ${isDarkMode ? 'border-border' : 'border-gray-200'}
          rounded-xl shadow-xl max-w-md relative
        `}
      >
        <div
          className={`
          flex items-center justify-between gap-4 px-4 py-3
          border-b ${isDarkMode ? 'border-border' : 'border-gray-200'}
        `}
        >
          <h3 className="font-semibold text-base flex-shrink-0">
            {typeof step.title === 'string' ? step.title : t('onboarding.tourTitle')}
          </h3>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className={`text-sm ${isDarkMode ? 'text-muted-foreground' : 'text-gray-500'}`}>
              {t('onboarding.stepCount', { current: currentStep, total: totalSteps })}
            </span>
            <button
              {...closeProps}
              className={`
                p-1 rounded-md
                ${isDarkMode ? 'hover:bg-muted' : 'hover:bg-gray-100'}
                transition-colors
              `}
              aria-label={typeof locale.close === 'string' ? locale.close : 'Close'}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="px-4 py-4">
          <div className={isDarkMode ? 'text-muted-foreground' : 'text-gray-700'}>{step.content}</div>
        </div>

        <div
          className={`
          flex flex-col gap-2 px-4 py-3
          border-t ${isDarkMode ? 'border-border' : 'border-gray-200'}
        `}
        >
          {isFirstRun && (
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none text-muted-foreground hover:text-foreground transition-colors">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="h-4 w-4 rounded border-border accent-primary cursor-pointer"
              />
              {t('onboarding.dontShowAgain')}
            </label>
          )}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {index > 0 && (
                <Button {...backProps} variant="ghost" size="sm">
                  {locale.back}
                </Button>
              )}
            </div>

            <div className="flex gap-2">
              {continuous && !isLastStep && (
                <Button {...skipProps} variant="ghost" size="sm">
                  {locale.skip}
                </Button>
              )}
              <Button {...primaryProps} size="sm">
                {isLastStep ? locale.last : locale.next}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Joyride
      key={`${safeStepIndex}-${resumeRequestedAt}`}
      steps={translatedSteps}
      run={run}
      stepIndex={safeStepIndex}
      continuous={tour.continuous ?? true}
      showProgress={false}
      showSkipButton={false}
      callback={handleJoyrideCallback}
      disableScrolling
      disableScrollParentFix
      spotlightPadding={4}
      tooltipComponent={CustomTooltip}
      styles={{
        options: {
          primaryColor: 'hsl(24.6 95% 53.1%)',
          zIndex: 10000,
          arrowColor: isDarkMode ? 'hsl(var(--card))' : '#ffffff',
        },
        overlay: {
          backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)',
        },
      }}
      locale={locale}
    />
  );
};
